/**
 * EmptyState — single source of truth for "nothing here" UI.
 *
 * Replaces the half-dozen ad-hoc empty divs scattered through EventList,
 * EventTimeline, AdminPanel, CommentsSection, MobileSidebar etc. Use the
 * `compact` variant for inline lists; the default for full-panel empties.
 */
import { ReactNode } from 'react';

interface EmptyStateProps {
  /** Optional emoji or SVG icon, sized 24px in compact / 40px in full. */
  icon?: ReactNode;
  /** Primary line — short, factual ("No active fires in view"). */
  title: string;
  /** Optional secondary line — explains why or what they can do next. */
  hint?: string;
  /** Optional action button / link. */
  action?: ReactNode;
  /**
   * Compact = inline within a list, smaller padding, no border. Default
   * is full-panel with generous padding.
   */
  compact?: boolean;
  className?: string;
}

export function EmptyState({
  icon,
  title,
  hint,
  action,
  compact = false,
  className = '',
}: EmptyStateProps) {
  const cls = `empty-state ${compact ? 'empty-state-compact' : ''} ${className}`.trim();
  return (
    <div className={cls} role="status">
      {icon && <div className="empty-state-icon" aria-hidden="true">{icon}</div>}
      <div className="empty-state-title">{title}</div>
      {hint && <div className="empty-state-hint">{hint}</div>}
      {action && <div className="empty-state-action">{action}</div>}
    </div>
  );
}

export default EmptyState;
